import classNames from "classnames"
import { Modal } from "common/Modal"
import { useNotifications } from "hooks/useNotification"
import { FormEvent, useEffect, useState } from "react"
import UserService from "services/user.service"
import { PackageList, PositionList, SeasonList, States } from "utils/constants"

const service = new UserService()

/** Same UX as `EditMediaAd`; submits to Vivint ecards `update-ad` route. */
export const EditVivintAdModal = ({
  showModal,
  selected,
  fetchData,
  setSelected,
  setShowModal,
}: {
  showModal: boolean
  selected: Record<string, any>
  fetchData: () => void
  setSelected: (v: Record<string, any>) => void
  setShowModal: (v: boolean) => void
}) => {
  const [state, setState] = useState({
    notes: "",
    ad_spend: "",
  })
  const [submitting, setSubmitting] = useState(false)

  const { successMessage, errorMessage } = useNotifications()

  const adId = selected?.id ?? selected?._id

  useEffect(() => {
    if (!showModal || !adId) return
    setState({
      notes: String(selected?.notes ?? ""),
      ad_spend: String(selected?.ad_spend ?? ""),
    })
  }, [showModal, adId])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!adId) return
    setSubmitting(true)
    service
      .updateVivintDashboardAd(String(adId), {
        ad_status: selected.ad_status,
        ad_approval_status: selected.ad_approval_status,
        ad_type: selected.ad_type,
        state: selected.state,
        season: selected.season,
        ad_amount: selected.ad_amount,
        position: selected.position,
        meet_link: selected.meet_link,
        package_start_date: selected.package_start_date,
        package_end_date: selected.package_end_date,
        city_name: selected.city_name,
        ...state,
      })
      .then(() => {
        fetchData()
        successMessage("Successfully updated")
      })
      .catch(() => {
        errorMessage("Could not update ad")
      })
      .finally(() => {
        setSubmitting(false)
        setShowModal(false)
      })
  }

  const calculateNewDate = (days: number, date: string | Date) => {
    const updatedDate = new Date(date)
    updatedDate.setDate(updatedDate.getDate() + days)
    return updatedDate
  }

  const onChangePackage = (
    days: number,
    packageName: string,
    amount: string | number
  ) => {
    setSelected({
      ...selected,
      package_start_date: selected.package_start_date,
      package_end_date: calculateNewDate(days, selected.package_start_date),
      package_name: packageName,
      ad_amount: amount,
    })
  }

  const handleChangeStart = (date: string) => {
    const currentDays =
      selected?.package_name === "PREMIUM"
        ? 5
        : selected?.package_name === "BASIC"
          ? 3
          : 4
    setSelected({
      ...selected,
      package_start_date: date,
      package_end_date: calculateNewDate(currentDays, date),
    })
  }

  return (
    <Modal
      width="w-full md:w-1/2"
      showModal={showModal}
      setShowModal={setShowModal}
      title="Update Vivint Ad"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
            <label className="field-label">Approval Status</label>
            <select
              onChange={(e) =>
                setSelected({
                  ...selected,
                  ad_approval_status: e.target.value,
                })
              }
              value={selected.ad_approval_status ?? ""}
              className="w-full rounded-md border border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
            >
              <option value="">Select status</option>
              <option value="UNDER REVIEW">UNDER REVIEW</option>
              <option value="DENIED">DENIED</option>
              <option value="APPROVED">APPROVED</option>
            </select>
          </div>
          <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
            <label className="field-label">Ad Status</label>
            <select
              onChange={(e) =>
                setSelected({
                  ...selected,
                  ad_status: e.target.value,
                })
              }
              value={selected.ad_status ?? ""}
              className="w-full rounded-md border border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
            >
              <option value="">Select status</option>
              <option value="In Process">In Process</option>
              <option value="Upcoming">Upcoming</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
          <label className="field-label">Ad Spend</label>
          <input
            type="text"
            value={state.ad_spend}
            onChange={(e) => setState({ ...state, ad_spend: e.target.value })}
            placeholder="$0.00"
            className="w-full rounded-md border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
          <label className="field-label">Meet Link</label>
          <input
            type="text"
            value={selected.meet_link ?? ""}
            onChange={(e) =>
              setSelected({ ...selected, meet_link: e.target.value })
            }
            placeholder="Link"
            className="w-full rounded-md border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
          />
        </div>
        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
            <label className="field-label">State</label>
            <select
              onChange={(e) =>
                setSelected({
                  ...selected,
                  state: e.target.value,
                })
              }
              value={selected.state ?? ""}
              className="w-full rounded-md border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
            >
              <option value="">Select State</option>
              {States.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
            <label className="field-label">City Name</label>
            <input
              type="text"
              value={selected.city_name ?? ""}
              onChange={(e) =>
                setSelected({ ...selected, city_name: e.target.value })
              }
              placeholder="City Name"
              className="w-full rounded-md border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
            />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
          <label className="field-label">Ad Type</label>
          <div className="flex gap-3">
            <span className="rounded-lg border border-black bg-ink hover:bg-gray-800 px-4 py-2 text-white">
              RECRUITING
            </span>
            <span className="cursor-not-allowed rounded-md border border-black px-4 py-2 opacity-50">
              SALES LEAD
            </span>
          </div>
        </div>
        <div>
          <h1 className="text-lg font-semibold text-zinc-800">Ad Details</h1>
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
              <label className="field-label">Season</label>
              <select
                onChange={(e) =>
                  setSelected({
                    ...selected,
                    season: e.target.value,
                  })
                }
                value={selected.season ?? ""}
                className="w-full rounded-md border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
              >
                <option value="">Select Season</option>
                {SeasonList.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
              <label className="field-label">Position</label>
              <select
                onChange={(e) =>
                  setSelected({
                    ...selected,
                    position: e.target.value,
                  })
                }
                value={selected.position ?? ""}
                className="w-full rounded-md border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
              >
                <option value="">Select Position</option>
                {PositionList.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-2 flex flex-1 flex-col gap-1.5 min-w-[150px]">
            <label className="field-label">Ad Package</label>
            <div className="flex gap-3">
              {PackageList.map((item) => (
                <div
                  key={item.day}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    onChangePackage(item.day, item.packageName, item.price)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      onChangePackage(item.day, item.packageName, item.price)
                  }}
                  className={classNames(
                    "flex flex-1 cursor-pointer flex-col rounded-md border border-black px-4 py-3 text-center hover:bg-black hover:text-white",
                    {
                      "bg-black text-white":
                        item.packageName === selected.package_name,
                    }
                  )}
                >
                  <span className="text-lg font-semibold">${item.price}</span>
                  <span>{item.day} Days</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-3 flex gap-3">
            <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
              <label className="field-label">Start Date</label>
              <input
                type="date"
                value={
                  selected?.package_start_date
                    ? new Date(selected.package_start_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={(e) => handleChangeStart(e.target.value)}
                className="w-full rounded-md border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
              />
            </div>
            <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
              <label className="field-label">End Date</label>
              <input
                type="date"
                disabled={true}
                value={
                  selected?.package_end_date
                    ? new Date(selected.package_end_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setSelected({
                    ...selected,
                    package_end_date: e.target.value,
                  })
                }
                className="w-full rounded-md border-gray-200 bg-gray-100 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0 disabled:bg-gray-100"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-1.5 min-w-[150px]">
          <label className="field-label">Admin Notes</label>
          <textarea
            value={state.notes}
            onChange={(e) => setState({ ...state, notes: e.target.value })}
            rows={4}
            className="w-full rounded-md border-gray-200 px-4 py-2 font-light text-gray-800 focus:border-gray-300 focus:outline-none focus:ring-0"
          />
        </div>

        <button type="submit" className="rounded-lg bg-ink hover:bg-gray-800 py-2 text-white">
          {submitting ? "Updating..." : "Update"}
        </button>
      </form>
    </Modal>
  )
}
